<template>
  <div class="billing-360-detail" data-testid="billing-360-detail">
    <div v-if="loading" class="billing-360-loading" data-testid="billing-360-loading">
      <i class="pi pi-spin pi-spinner" />
      {{ t('Carregando…') }}
    </div>

    <div v-else-if="error" class="billing-360-error" data-testid="billing-360-error">
      {{ error }}
    </div>

    <template v-else-if="dados">
      <Tabs v-model:value="activeTab" :scrollable="false">
        <TabList>
          <Tab :value="0" data-testid="billing-360-tab-assinatura">{{ t('Assinaturas') }}</Tab>
          <Tab :value="1" data-testid="billing-360-tab-grace">{{ t('Períodos de Graça') }}</Tab>
          <Tab :value="2" data-testid="billing-360-tab-creditos">{{ t('Créditos') }}</Tab>
          <Tab :value="3" data-testid="billing-360-tab-transacoes">{{ t('Transações') }}</Tab>
          <Tab v-if="isStaff" :value="4" data-testid="billing-360-tab-eventos">{{ t('Eventos') }}</Tab>
          <Tab :value="5" data-testid="billing-360-tab-setup-fee">{{ t('Taxa de Implantação') }}</Tab>
        </TabList>

        <TabPanels>
          <TabPanel :value="0" data-testid="billing-360-panel-assinatura">
            <div v-if="!isStaff && setupFeeStatus?.status === 'owed'" class="mb-3">
              <inline-message severity="warn" data-testid="user-billing-360-setup-fee-blocked">
                {{ t('A taxa de implantação deve ser quitada antes de contratar ou alterar assinaturas.') }}
              </inline-message>
              <Button
                :label="t('Ver taxa de implantação')"
                icon="pi pi-arrow-right"
                size="small"
                severity="primary"
                class="mt-2"
                data-testid="user-billing-360-goto-setup-fee-btn"
                @click="activeTab = 5"
              />
            </div>
            <!-- 'Criar assinatura' is hidden once the franchise HAS one:
                 POST /subscriptions/subscribe refuses an active|grace franchise
                 ('Franquia já possui assinatura ativa'), so offering the button
                 only walks the user into a refusal (reported 2026-08-20). The
                 table right below already shows the live subscription. -->
            <div
              v-if="(isStaff || setupFeeStatus?.status !== 'owed') && !franchiseHasLiveSubscription"
              class="billing-360-toolbar"
            >
              <Button
                :label="t('Criar assinatura')"
                icon="pi pi-plus"
                size="small"
                severity="primary"
                data-testid="billing-360-create-subscription-btn"
                @click="handleCreateSubscription"
              />
            </div>
            <div v-if="subscriptionsDisplayRows.length > 0" class="billing-360-section">
              <data-table
                :config="subscriptionTableConfig"
                :data="subscriptionsDisplayRows"
                :refreshButton="{ show: true }"
                data-testid="billing-360-subscription-table"
                @refresh="reloadAll"
                @edit-click="handleSubscriptionEditClick"
                @button-click="handleSubscriptionButtonClick"
              />
            </div>
            <div v-else class="billing-360-empty">
              {{ t('Sem assinaturas registradas') }}
            </div>
          </TabPanel>

          <TabPanel :value="1" data-testid="billing-360-panel-grace">
            <div v-if="isStaff" class="billing-360-toolbar">
              <Button
                :label="t('Acrescentar período')"
                icon="pi pi-plus"
                size="small"
                severity="primary"
                data-testid="billing-360-add-grace-btn"
                @click="handleAddGrace"
              />
            </div>
            <div v-if="dados.grace_windows.length > 0" class="billing-360-section">
              <data-table
                :config="graceConfig"
                :data="graceDisplayRows"
                :refreshButton="{ show: true }"
                data-testid="billing-360-grace-table"
                @refresh="reloadAll"
                @edit-click="handleGraceEditClick"
                @button-click="handleGraceButtonClick"
              />
            </div>
            <div v-else class="billing-360-empty">
              {{ t('Sem janelas de carência registradas') }}
            </div>
          </TabPanel>

          <TabPanel :value="2" data-testid="billing-360-panel-creditos">
            <div v-if="isStaff" class="billing-360-toolbar">
              <Button
                :label="t('Adicionar créditos')"
                icon="pi pi-plus"
                size="small"
                severity="primary"
                data-testid="billing-360-add-credits-btn"
                @click="handleAddCredits"
              />
            </div>

            <!-- The franqueado BUYS credits (staff GRANT them, above). Same
                 pack modal for every surface, so the catalog, the market
                 gating and the checkout are one implementation.
                 purchaseEnabled is OD-016: null until the modal has loaded the
                 catalog once, false when the market has no packs. -->
            <div v-else class="billing-360-toolbar">
              <Button
                :label="t('Comprar créditos')"
                icon="pi pi-bolt"
                size="small"
                severity="primary"
                :disabled="purchaseEnabled === false"
                data-testid="user-billing-360-buy-credits-btn"
                @click="handleBuyCredits"
              />
              <span
                v-if="purchaseEnabled === false"
                class="billing-360-hint"
                data-testid="user-billing-360-buy-credits-disabled-hint"
              >
                {{ t('Compra de créditos indisponível no momento.') }}
              </span>
            </div>

            <div v-if="dados.ledger.length > 0" class="billing-360-section">
              <data-table
                :config="ledgerConfig"
                :data="ledgerDisplayRows"
                :refreshButton="{ show: true }"
                data-testid="billing-360-ledger-table"
                @refresh="reloadAll"
                @edit-click="handleCreditEditClick"
              />
            </div>
            <div v-else class="billing-360-empty">
              {{ t('Sem entradas no ledger') }}
            </div>
          </TabPanel>

          <TabPanel :value="3" data-testid="billing-360-panel-transacoes">
            <div v-if="dados.transactions.length > 0" class="billing-360-section">
              <data-table
                :config="txTableConfig"
                :data="txDisplayRows"
                :refreshButton="{ show: true }"
                data-testid="billing-360-transactions-table"
                @refresh="reloadAll"
                @edit-click="handleTxEditClick"
              />
            </div>
            <div v-else class="billing-360-empty">
              {{ t('Sem transações registradas') }}
            </div>
          </TabPanel>

          <TabPanel v-if="isStaff" :value="4" data-testid="billing-360-panel-eventos">
            <div v-if="dados.events.length > 0">
              <div
                v-for="ev in evDisplayRows"
                :key="ev.id"
                class="billing-360-event-block"
              >
                <div
                  class="billing-360-event-header"
                  :data-testid="`billing-360-event-${ev.id}`"
                  @click="toggleEvent(ev.id)"
                >
                  <span class="billing-360-event-type">{{ ev.event_type ?? '—' }}</span>
                  <span class="billing-360-event-status">{{ ev.status ?? '—' }}</span>
                  <span class="billing-360-event-provider">{{ ev.provider ?? '—' }}</span>
                  <span class="billing-360-event-date">{{ ev.created_at_display }}</span>
                  <i
                    :class="expandedEvent.has(ev.id) ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
                    class="billing-360-event-chevron"
                  />
                </div>

                <div
                  v-if="expandedEvent.has(ev.id)"
                  class="billing-360-event-payload"
                >
                  <div v-if="ev.processed_at_display" class="billing-360-event-meta">
                    {{ t('Processado em') }}: {{ ev.processed_at_display }}
                  </div>
                  <div v-if="ev.error" class="billing-360-event-error">
                    {{ t('Erro') }}: {{ ev.error }}
                  </div>

                  <div
                    v-if="ev.status === 'failed' || ev.status === 'dead'"
                    class="billing-360-event-actions"
                  >
                    <Button
                      :label="t('Reprocessar')"
                      icon="pi pi-refresh"
                      size="small"
                      severity="secondary"
                      :loading="reprocessingIds.has(ev.id)"
                      :disabled="reprocessingIds.has(ev.id)"
                      :data-testid="`billing-360-event-reprocess-${ev.id}`"
                      @click="handleReprocess(ev.id)"
                    />
                  </div>

                  <pre v-if="ev.payload" class="billing-360-event-json">{{ prettyJson(ev.payload) }}</pre>
                  <div v-else class="billing-360-empty">{{ t('Sem payload') }}</div>
                </div>
              </div>
            </div>
            <div v-else class="billing-360-empty">
              {{ t('Sem eventos registrados') }}
            </div>
          </TabPanel>

          <TabPanel :value="5" data-testid="billing-360-panel-setup-fee">
            <div class="billing-360-toolbar">
              <template v-if="isStaff">
                <template v-if="setupFeeSatisfied">
                  <Button
                    :label="t('Re-exigir taxa')"
                    icon="pi pi-refresh"
                    size="small"
                    severity="warn"
                    data-testid="billing-360-setup-fee-rerequire"
                    @click="handleRerequireSetupFee"
                  />
                </template>
                <template v-else>
                  <Button
                    :label="t('Emitir taxa')"
                    icon="pi pi-plus"
                    size="small"
                    severity="primary"
                    data-testid="billing-360-setup-fee-emit"
                    @click="handleEmitSetupFee"
                  />
                  <Button
                    :label="t('Isentar taxa')"
                    icon="pi pi-shield"
                    size="small"
                    severity="info"
                    outlined
                    data-testid="billing-360-setup-fee-exempt"
                    @click="handleExemptSetupFee"
                  />
                </template>
              </template>
              <template v-else>
                <template v-if="setupFeeStatus?.status === 'owed'">
                  <Button
                    :label="t('Pagar taxa de implantação')"
                    icon="pi pi-credit-card"
                    size="small"
                    severity="primary"
                    :loading="checkingOutFranquiaId === franquiaId"
                    data-testid="user-billing-360-pay-setup-fee-btn"
                    @click="handlePaySetupFee"
                  />
                </template>
                <template v-else-if="setupFeeStatus?.status === 'paid'">
                  <span class="p-tag p-tag-success" data-testid="user-billing-360-setup-fee-paid-tag">
                    <i class="pi pi-check mr-1" />
                    {{ t('Taxa de implantação quitada') }}
                  </span>
                </template>
                <template v-else-if="setupFeeStatus?.status === 'exempt'">
                  <span class="p-tag p-tag-info" data-testid="user-billing-360-setup-fee-exempt-tag">
                    <i class="pi pi-shield mr-1" />
                    {{ t('Isento da taxa de implantação') }}
                  </span>
                </template>
              </template>
            </div>

            <div class="billing-360-section">
              <div v-if="setupFeeDisplayRows.length > 0">
                <data-table
                  :config="setupFeeTableConfig"
                  :data="setupFeeDisplayRows"
                  :refreshButton="{ show: true }"
                  data-testid="billing-360-setup-charges-table"
                  @refresh="reloadAll"
                  @edit-click="handleSetupFeeEditClick"
                />
              </div>
              <div v-else class="billing-360-empty">
                {{ t('Sem cobranças de taxa registradas') }}
              </div>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </template>

    <div
      v-else-if="!loading && franquiaId"
      class="billing-360-empty"
      data-testid="billing-360-no-data"
    >
      {{ t('Sem dados de billing para esta franquia') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watch, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useBillingThreeSixty } from '../../../composables/useBillingThreeSixty'
import { useReconciliationSync } from '../../../composables/useReconciliationSync'
import { useSetupFee } from '../../../composables/useSetupFee'
import { useSetupFeeCheckout } from '../../../composables/useSetupFeeCheckout'
import { useCreditPurchase } from '../../../composables/useCreditPurchase'
import { useDateFormatter } from '../../../composables/useDateFormatter'
import { currencyFactor } from '../../../utils/currencyMinorUnits'
import { isLiveSubscriptionRow } from '../../../utils/subscriptionLiveStatus'
import { formatCredits } from '../../assistant/formatCredits'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import CompactDataTable from '../../../grid/CompactDataTable.vue'
import Button from '../../../ui/AppButton.vue'
import type { SetupFee_StatusResponseDados } from '../../../contracts/SetupFee_StatusResponse'
import { isSetupSatisfied } from '../../../utils/setupFeeStatus'
import type { Grace_PeriodRow } from '../../../contracts/Grace_PeriodRow'
import type { Subscriptions_Billing360GraceRow } from '../../../contracts/Subscriptions_Billing360GraceRow'
import type { Subscriptions_AdminSubscriptionRow } from '../../../contracts/Subscriptions_AdminSubscriptionRow'

const props = withDefaults(
  defineProps<{
    franquiaId: number
    isStaff?: boolean
    initialTab?: string | number
  }>(),
  {
    isStaff: true,
  },
)

const { t, n } = useI18n()
const { dados, loading, error, fetchBilling360, reprocessEvent } = useBillingThreeSixty()
const { syncSubscription, syncSetupFee } = useReconciliationSync()
const { fetchStatus: fetchSetupFeeStatus } = useSetupFee()
const { checkoutAndRedirect, checkingOutFranquiaId } = useSetupFeeCheckout()
// OD-016: purchaseEnabled is set by listAvailablePacks() inside PackPurchaseModal;
// read here to gate the buy CTA without an extra round-trip.
const { purchaseEnabled } = useCreditPurchase()
const { fromApiDateTimeFormat, formatDate, formatDateTime } = useDateFormatter()

const activeTab = ref(0)
const setupFeeStatus = ref<SetupFee_StatusResponseDados | null>(null)
const isSyncingSetupFee = ref(false)
const syncingSubscriptionIds = reactive(new Set<number>())

async function loadSetupFeeStatus(fid: number): Promise<void> {
  if (!fid) return
  try {
    setupFeeStatus.value = await fetchSetupFeeStatus(fid)
  } catch (err) {
    log('BillingThreeSixtyDetail', 'loadSetupFeeStatus', 'Erro ao carregar status da taxa', { err })
    setupFeeStatus.value = null
  }
}

async function loadFranchise(franchiseId: number): Promise<void> {
  if (!franchiseId) return
  if (props.initialTab === 'setup_fee' || props.initialTab === 5) {
    activeTab.value = 5
  } else if (typeof props.initialTab === 'number') {
    activeTab.value = props.initialTab
  } else {
    activeTab.value = 0
  }
  await Promise.all([
    fetchBilling360(franchiseId, props.isStaff),
    loadSetupFeeStatus(franchiseId),
  ])
}


async function handlePaySetupFee(): Promise<void> {
  if (!props.franquiaId) return
  try {
    const { redirected, dados: checkoutDados } = await checkoutAndRedirect(props.franquiaId)
    if (!redirected && checkoutDados?.checkout_url) {
      window.open(checkoutDados.checkout_url, '_blank')
    }
  } catch (err) {
    log('BillingThreeSixtyDetail', 'handlePaySetupFee', 'Erro ao iniciar checkout de setup', err)
    eventBus.emit('globalMessage.error', { message: (err as Error)?.message })
  }
}


watch(
  () => props.franquiaId,
  (id) => {
    loadFranchise(Number(id))
  },
  { immediate: true },
)

function formatD(iso?: string): string {

  return iso ? formatDate(fromApiDateTimeFormat(iso)) : '—'
}

function formatDt(iso?: string): string {
  return iso ? formatDateTime(fromApiDateTimeFormat(iso)) : '—'
}

function prettyJson(raw?: string): string {
  if (!raw) return ''
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}

function providerLabel(provider: string | null | undefined): string {
  if (!provider) return '—'
  const map: Record<string, string> = {
    asaas: 'Asaas',
    stripe: 'Stripe',
    fake: 'Fake',
  }
  return map[provider.toLowerCase()] ?? (provider.charAt(0).toUpperCase() + provider.slice(1))
}

function subscriptionStatusLabel(status: string | null | undefined): string {
  const labels: Record<string, string> = {
    active:     t('Ativa'),
    grace:      t('Carência'),
    cancelled:  t('Cancelada'),
    superseded: t('Substituída'),
    pending:    t('Pendente'),
  }
  if (!status || status === 'none') return t('Sem assinatura')
  return labels[status] ?? status
}

function subscriptionStatusSeverity(status: string | null | undefined): string {
  const severities: Record<string, string> = {
    active:     'success',
    grace:      'warn',
    cancelled:  'secondary',
    superseded: 'secondary',
    pending:    'info',
  }
  if (!status || status === 'none') return 'secondary'
  return severities[status] ?? 'secondary'
}

/**
 * Does THIS franchise already carry a live subscription? Uses the shared
 * predicate (src/utils/subscriptionLiveStatus) so this screen and the subscribe
 * modal's franchise picker answer with the backend's own rule.
 *
 * The rows carry no franchise_id of their own here — the whole payload is this
 * one franchise — so the row list is matched directly rather than by id.
 *
 * These rows name the status `effective_status`; only /subscriptions/mine calls
 * it `status`. The predicate reads both, so this gate is no longer a cast away
 * from reading `undefined` on every row and gating nothing (2026-08-21).
 */
const franchiseHasLiveSubscription = computed(() => {
  const list = dados.value?.subscriptions?.length
    ? dados.value.subscriptions
    : (dados.value?.subscription ? [dados.value.subscription] : [])
  return list.some((sub) => isLiveSubscriptionRow(sub))
})

const subscriptionsDisplayRows = computed(() => {
  const list = dados.value?.subscriptions?.length
    ? dados.value.subscriptions
    : (dados.value?.subscription ? [dados.value.subscription] : [])

  return list.map((sub) => {
    let priceDisplay = ''
    if (sub.price_cents != null) {
      const val = sub.price_cents / 100
      const curr = (sub.currency && /^[A-Z]{3}$/i.test(sub.currency)) ? sub.currency.toUpperCase() : 'BRL'
      try {
        priceDisplay = n(val, { style: 'currency', currency: curr })
      } catch {
        priceDisplay = `${val}`
      }
    } else if (sub.currency) {
      priceDisplay = sub.currency
    }


    return {
      ...sub,
      id: sub.subscription_id,
      plan_display: sub.plan_name || (sub.plan_id ? String(sub.plan_id) : '—'),
      price_display: priceDisplay || '—',
      provider_display: providerLabel(sub.provider),
      current_period_end_display: sub.current_period_end ? formatDt(sub.current_period_end) : '—',
      created_at_display: sub.created_at ? formatDt(sub.created_at) : '—',
      is_syncing: syncingSubscriptionIds.has(sub.subscription_id),
    }

  })
})

const activeCanReconcileSubId = computed(() => {
  if (dados.value?.subscription?.can_reconcile === true) {
    return dados.value.subscription.subscription_id
  }
  const canRec = dados.value?.subscriptions?.find((s) => s.can_reconcile === true)
  return canRec ? canRec.subscription_id : null
})

const subscriptionTableConfig = computed(() => ({
  tableId: 'billing360SubscriptionsTable',
  columns: [
    {
      data: 'id',
      title: t('ID'),
      sortable: true,
      renderType: 'clickable_id',
    },
    {
      data: 'plan_display',
      title: t('Plano'),
      sortable: true,
    },

    {
      data: 'price_display',
      title: t('Valor'),
      sortable: true,
    },
    {
      data: 'provider_display',
      title: t('Gateway'),
      sortable: true,
    },
    {
      data: 'raw_status',
      title: t('Status'),
      sortable: true,
      renderType: 'badge',
      badgeConfig: (row: Record<string, unknown>) => {
        const status = (row.raw_status || row.effective_status) as string | undefined
        return {
          label: subscriptionStatusLabel(status),
          severity: subscriptionStatusSeverity(status),
          tooltip: subscriptionStatusLabel(status),
        }
      },
    },
    {
      data: 'current_period_end_display',
      title: t('Fim do Período'),
      sortable: true,
    },
    {
      data: 'created_at_display',
      title: t('Criado em'),
      sortable: true,
    },
    // The whole 'Ações' column is STAFF-ONLY (2026-08-20). Gating just the
    // button left the user 360 with an empty column and its header; the column
    // itself is what does not belong to the franqueado's read surface.
    ...(props.isStaff
      ? [{
          data: 'subscription_id',
          title: t('Ações'),
          sortable: false,
          renderType: 'clickable_button',
          buttonText: t('Sincronizar'),
          buttonIcon: 'pi pi-sync',
          buttonClass: 'p-button-sm p-button-secondary p-button-outlined',
          buttonAction: 'sync',
          buttonTestId: 'billing-360-subscription-sync',
          // syncSubscription posts to the Full-only ADMIN endpoint; the
          // per-button check stays as defence in depth behind the column gate.
          buttonCondition: (row: Record<string, unknown>) => props.isStaff && row.can_reconcile === true,
          buttonDisabled: (row: Record<string, unknown>) => row.is_syncing === true,
        }]
      : []),
  ],
  pagination: false,
}))

function handleSubscriptionEditClick(id: unknown): void {
  const subId = Number(id)
  if (!subId) return
  const sub = dados.value?.subscriptions?.find((s) => Number(s.subscription_id) === subId)
    ?? (dados.value?.subscription?.subscription_id === subId ? dados.value.subscription : null)
  if (!sub) return

  eventBus.emit('modal.subscriptionDetail.open', {
    // The user 360 opens the FRANQUEADO view — staff view only in staff mode.
    staff: props.isStaff,
    row: {
      subscription_id: sub.subscription_id,
      franquia_id: Number(props.franquiaId),
      franchise_name: '',
      plan_id: sub.plan_id ?? null,
      plan_name: sub.plan_name ?? null,
      provider: sub.provider,
      currency: sub.currency ?? null,
      status: sub.raw_status || sub.effective_status,
      started_at: sub.created_at,
      current_period_end: sub.current_period_end,
      grace_until: sub.grace_until,
      created_at: sub.created_at,
      can_reconcile: sub.can_reconcile,
    } as unknown as Subscriptions_AdminSubscriptionRow,
  })
}

function handleSubscriptionButtonClick(event: { value?: unknown; data?: Record<string, unknown>; action?: string }): void {
  if (event?.action === 'sync') {
    const subId = Number(event.data?.subscription_id || event.data?.id || event.value)
    if (subId) {
      handleSyncSubscription(subId)
    }
  }
}




function handleCreateSubscription(): void {
  eventBus.emit('modal.subscribe.open', { franquia_id: Number(props.franquiaId) })
}

function handleAddGrace(): void {
  eventBus.emit('modal.graceGrant.open', { franchise_id: Number(props.franquiaId) })
}

/**
 * The franqueado's own purchase door — same modal (and therefore same catalog,
 * market gating and checkout) as every other pack-purchase CTA.
 */
function handleBuyCredits(): void {
  eventBus.emit('modal.pack-purchase.open', { franquia_id: Number(props.franquiaId) })
}

function handleAddCredits(): void {
  eventBus.emit('modal.staff-credits-topup.open', {
    franquia_id: Number(props.franquiaId),
    franquiaId: Number(props.franquiaId),
  })
}

function handleCreditEditClick(id: unknown): void {
  const creditId = Number(id)
  if (!creditId) return
  const row = dados.value?.ledger?.find((l) => Number(l.id) === creditId)
  if (row) {
    eventBus.emit('modal.credit-transaction.open', {
      id: row.id,
      franquia_id: Number(props.franquiaId),
      franchise_name: `Franquia #${props.franquiaId}`,
      entry_type: row.entry_type,
      signed_micro: row.signed_micro,
      balance_after_micro: row.signed_micro,
      request_id: row.request_id,
      created_at: row.created_at,
    })
  }
}


const graceSourceBadgeMap: Record<string, { label: string; severity: string }> = {
  staff: { label: t('Staff'), severity: 'info' },
  payment_overdue: { label: t('Inadimplência'), severity: 'warn' },
  backfill: { label: t('Migração'), severity: 'secondary' },
}

function graceSourceBadgeConfig(row: Record<string, unknown>) {
  const source = row.source as string | undefined
  const cfg = source ? (graceSourceBadgeMap[source] ?? { label: source, severity: 'secondary' }) : { label: '—', severity: 'secondary' }
  return { label: cfg.label, severity: cfg.severity, tooltip: cfg.label }
}

function graceSituacaoBadgeConfig(row: Record<string, unknown>) {
  if (row.is_live === true || !row.revoked_at) {
    return { label: t('Vigente'), severity: 'success', tooltip: t('Vigente') }
  }
  if (row.revoked_at != null) {
    return { label: t('Revogado'), severity: 'danger', tooltip: t('Revogado') }
  }
  return { label: t('Expirado'), severity: 'secondary', tooltip: t('Expirado') }
}

const graceDisplayRows = computed(() =>
  (dados.value?.grace_windows ?? []).map((row) => ({
    ...row,
    id: row.id,
    starts_at_display: formatD(row.starts_at),
    ends_at_display: formatD(row.ends_at),
    revoked_at_display: formatDt(row.revoked_at),
    is_live: row.revoked_at === null,
  })),
)

const graceConfig = computed(() => ({
  tableId: 'billing360GraceTable',
  columns: [
    {
      data: 'id',
      title: t('ID'),
      sortable: true,
      renderType: 'clickable_id',
    },
    {
      data: 'source',
      title: t('Origem'),
      sortable: true,
      renderType: 'badge',
      badgeConfig: graceSourceBadgeConfig,
    },
    {
      data: 'starts_at_display',
      title: t('Início'),
      sortable: true,
    },
    {
      data: 'ends_at_display',
      title: t('Fim'),
      sortable: true,
    },
    {
      data: 'granted_by',
      title: t('Concedido por'),
      sortable: true,
    },
    {
      data: 'reason',
      title: t('Motivo'),
      sortable: false,
    },
    {
      data: 'id',
      title: t('Situação'),
      sortable: false,
      renderType: 'badge',
      badgeConfig: graceSituacaoBadgeConfig,
    },
    // STAFF-ONLY column (2026-08-20). 'Revogar' had NO group gate at all, so a
    // franqueado was offered a button POST /subscriptions/grace/revoke refuses
    // outright (assertFullAccess) — revoking your own carência is not a
    // self-service act.
    ...(props.isStaff
      ? [{
          data: 'id',
          title: t('Ações'),
          sortable: false,
          renderType: 'clickable_button',
          buttonText: t('Revogar'),
          buttonIcon: 'pi pi-times',
          buttonClass: 'p-button-sm p-button-danger p-button-outlined',
          buttonAction: 'revoke',
          buttonTestId: 'billing-360-grace-revoke-btn',
          buttonCondition: (row: Record<string, unknown>) => row.is_live === true || !row.revoked_at,
        }]
      : []),
  ],
  pagination: false,
}))

/**
 * Translate a billing-360 grace window into the row GraceRevokeModal consumes.
 *
 * The two payloads name the same things differently — the 360 row carries `id`,
 * Grace_PeriodRow requires `grace_id` and `franquia_id`. This used to be built
 * inline (twice) with `id` / `franchise_id` and an `as Grace_PeriodRow` cast on
 * the end, which silenced the mismatch: the modal then read `row.grace_id` as
 * undefined and POSTed grace_id=0, so every revoke from this screen came back
 * `grace_id must be a positive integer` and surfaced as the generic "Erro ao
 * revogar carência". The empty franchise name in the modal was the same cast,
 * showing (2026-08-21).
 *
 * No cast: the return annotation is what makes TypeScript check the mapping, so
 * the next renamed field is a compile error instead of a dead button.
 */
function toGraceRevokeRow(g: Subscriptions_Billing360GraceRow): Grace_PeriodRow {
  return {
    grace_id: Number(g.id),
    franquia_id: Number(props.franquiaId),
    // The 360 payload carries no franchise name; this screen's other surfaces
    // use the same #id fallback.
    franchise_name: `Franquia #${props.franquiaId}`,
    subscription_id: g.subscription_id ?? undefined,
    plan_id: g.plan_id ?? undefined,
    source: g.source ?? undefined,
    granted_by: g.granted_by ?? undefined,
    reason: g.reason ?? undefined,
    starts_at: g.starts_at ?? undefined,
    ends_at: g.ends_at ?? undefined,
    revoked_at: g.revoked_at ?? undefined,
    is_live: g.revoked_at == null,
  }
}

function openGraceRevoke(graceId: number): void {
  if (!graceId) return
  const g = dados.value?.grace_windows.find((gw) => Number(gw.id) === graceId)
  if (!g) return
  eventBus.emit('modal.graceRevoke.open', { row: toGraceRevokeRow(g) })
}

function handleGraceEditClick(id: unknown): void {
  openGraceRevoke(Number(id))
}

function handleGraceButtonClick(event: { value?: unknown; data?: Record<string, unknown>; action?: string }): void {
  if (event?.action === 'revoke') {
    openGraceRevoke(Number(event.data?.id || event.value))
  }
}

const creditsRows = computed(() => {
  const credits = dados.value?.credits
  if (!credits) {
    return [{ prop: t('Saldo'), value: t('Sem carteira de créditos') }]
  }
  const balanceMicro = credits.balance_micro ?? 0
  const reservedMicro = credits.reserved_micro ?? 0
  return [
    { prop: t('Saldo'), value: `${formatCredits(balanceMicro)} ${t('cr')}` },
    { prop: t('Reservado'), value: `${formatCredits(reservedMicro)} ${t('cr')}` },
    { prop: t('Disponível'), value: `${formatCredits(Math.max(0, balanceMicro - reservedMicro))} ${t('cr')}` },
  ]
})

/**
 * The ledger's REAL entry_type vocabulary, from ai_credit_ledger_model:
 * reserve / settle / release / topup / meter / adjust, plus 'grant:<source>'.
 *
 * The previous map guessed at purchase/usage/manual_adjustment/refund — none of
 * which the backend ever writes — so every row except 'topup' rendered its raw
 * code at the user (reported 2026-08-20).
 */
const ledgerEntryTypeMap: Record<string, { label: string; severity: string }> = {
  // The hold taken before a run, and its two possible endings.
  reserve: { label: t('Reserva'), severity: 'info' },

  settle:  { label: t('Consumo'), severity: 'secondary' },
  release: { label: t('Reserva liberada'), severity: 'secondary' },
  // Metered usage that does NOT debit (Full/Master callers, by design).
  meter:   { label: t('Uso medido'), severity: 'secondary' },
  topup:   { label: t('Recarga'), severity: 'success' },
  adjust:  { label: t('Ajuste manual'), severity: 'warn' },
}

/** 'grant:<source>' — free credits. The source says WHY they were granted. */
const ledgerGrantSourceMap: Record<string, string> = {
  subscription_grant: t('Créditos do plano'),
  purchase: t('Compra de pacote'),
}

function ledgerEntryTypeLabel(type: string): { label: string; severity: string } {
  if (type.startsWith('grant:')) {
    const source = type.slice('grant:'.length)
    return {
      // An unmapped source still reads as a grant — never as a raw code.
      label: ledgerGrantSourceMap[source] ?? `${t('Concessão')} (${source})`,
      severity: 'success',
    }
  }
  return ledgerEntryTypeMap[type] ?? { label: type, severity: 'secondary' }
}

function ledgerEntryTypeBadgeConfig(row: Record<string, unknown>) {
  const type = row.entry_type as string | undefined
  const cfg = type ? ledgerEntryTypeLabel(type) : { label: '—', severity: 'secondary' }
  return { label: cfg.label, severity: cfg.severity, tooltip: cfg.label }
}

const ledgerDisplayRows = computed(() =>
  (dados.value?.ledger ?? []).map((row) => {
    const signed = row.signed_micro ?? 0
    const prefix = signed > 0 ? '+' : ''
    return {
      ...row,
      id: row.id,
      signed_display: `${prefix}${formatCredits(signed)} ${t('cr')}`,
      created_at_display: formatDt(row.created_at),
    }
  }),
)

const ledgerConfig = computed(() => ({
  tableId: 'billing360LedgerTable',
  columns: [
    {
      data: 'id',
      title: t('ID'),
      sortable: true,
      renderType: 'clickable_id',
    },
    {
      data: 'created_at_display',
      title: t('Data'),
      sortable: true,
    },
    {
      data: 'entry_type',
      title: t('Tipo'),
      sortable: true,
      renderType: 'badge',
      badgeConfig: ledgerEntryTypeBadgeConfig,
    },
    {
      data: 'signed_display',
      title: t('Movimentação'),
      sortable: true,
    },
    {
      data: 'request_id',
      title: t('Req ID'),
      sortable: true,
    },
  ],
  pagination: true,
  pageLength: 20,
}))

const expandedTx = reactive(new Set<number>())

function toggleTx(id: number): void {
  if (expandedTx.has(id)) {
    expandedTx.delete(id)
  } else {
    expandedTx.add(id)
  }
}

const txKindBadgeMap: Record<string, { label: string; severity: string }> = {
  // 'cycle' is what platform_revenue_transactions actually stores for a
  // subscription cycle charge — without this entry the badge leaked the raw
  // string (user report 2026-08-20).
  cycle: { label: t('Mensalidade'), severity: 'info' },
  charge: { label: t('Mensalidade'), severity: 'info' },
  credit_purchase: { label: t('Créditos IA'), severity: 'success' },
  setup_fee: { label: t('Taxa de Implantação'), severity: 'warn' },
}

function txKindBadgeConfig(row: Record<string, unknown>) {
  const kind = row.kind as string | undefined
  const cfg = kind ? (txKindBadgeMap[kind] ?? { label: kind, severity: 'secondary' }) : { label: '—', severity: 'secondary' }
  return { label: cfg.label, severity: cfg.severity, tooltip: cfg.label }
}

const txStatusBadgeMap: Record<string, { label: string; severity: string }> = {
  confirmed: { label: t('Confirmada'), severity: 'success' },
  pending: { label: t('Pendente'), severity: 'warn' },
  failed: { label: t('Falhou'), severity: 'danger' },
  refunded: { label: t('Estornada'), severity: 'secondary' },
}

function txStatusBadgeConfig(row: Record<string, unknown>) {
  const status = row.status as string | undefined
  const cfg = status ? (txStatusBadgeMap[status] ?? { label: status, severity: 'secondary' }) : { label: '—', severity: 'secondary' }
  return { label: cfg.label, severity: cfg.severity, tooltip: cfg.label }
}

const txDisplayRows = computed(() =>
  (dados.value?.transactions ?? []).map((tx) => {
    const currency = (tx.currency && /^[A-Z]{3}$/i.test(tx.currency)) ? tx.currency.toUpperCase() : 'BRL'
    let formattedAmount = ''
    try {
      formattedAmount = n(
        (tx.amount_cents ?? 0) / currencyFactor('pt-BR', currency),
        { style: 'currency', currency },
      )
    } catch {
      formattedAmount = `${(tx.amount_cents ?? 0) / 100}`
    }
    return {
      ...tx,
      id: tx.id,
      currency,
      amount_display: formattedAmount,
      amount_brl: formattedAmount,
      provider_display: providerLabel(tx.provider),
      paid_at_display: tx.paid_at ? formatDt(tx.paid_at) : '—',
      created_at_display: formatDt(tx.created_at),
    }
  }),
)


const txTableConfig = computed(() => ({
  tableId: 'billing360TxTable',
  columns: [
    {
      data: 'id',
      title: t('ID'),
      sortable: true,
      renderType: 'clickable_id',
    },
    {
      data: 'kind',
      title: t('Tipo'),
      sortable: true,
      renderType: 'badge',
      badgeConfig: txKindBadgeConfig,
    },
    {
      data: 'amount_display',
      title: t('Valor'),
      sortable: true,
    },
    {
      data: 'provider_display',
      title: t('Gateway'),
      sortable: true,
    },
    {
      data: 'status',
      title: t('Status'),
      sortable: true,
      renderType: 'badge',
      badgeConfig: txStatusBadgeConfig,
    },
    {
      data: 'paid_at_display',
      title: t('Pago em'),
      sortable: true,
    },
    {
      data: 'created_at_display',
      title: t('Criado em'),
      sortable: true,
    },
  ],
  pagination: false,
}))


function handleTxEditClick(id: unknown): void {
  const txId = Number(id)
  if (!txId) return
  const tx = txDisplayRows.value.find((t) => Number(t.id) === txId)
  if (tx) {
    eventBus.emit('modal.staffTransaction.open', {
      row: {
        id: tx.id,
        franquia_id: Number(props.franquiaId),
        franchise_name: `Franquia #${props.franquiaId}`,
        kind: tx.kind === 'charge' ? 'cycle' : tx.kind,
        amount_cents: tx.amount_cents,
        amount_display: tx.amount_display,
        currency: tx.currency,
        status: tx.status,
        provider: tx.provider,
        provider_charge_id: tx.provider_charge_id,
        paid_at: tx.paid_at,
        paid_at_display: tx.paid_at_display,
        created_at: tx.created_at,
        created_at_display: tx.created_at_display,
        attempts: tx.attempts ?? [],
      },
    })
  }
}


const expandedEvent = reactive(new Set<number>())

function toggleEvent(id: number): void {
  if (expandedEvent.has(id)) {
    expandedEvent.delete(id)
  } else {
    expandedEvent.add(id)
  }
}

const evDisplayRows = computed(() =>
  (dados.value?.events ?? []).map((ev) => ({
    ...ev,
    created_at_display: formatDt(ev.created_at),
    processed_at_display: ev.processed_at ? formatDt(ev.processed_at) : null,
  })),
)

const reprocessingIds = reactive(new Set<number>())

async function handleReprocess(eventId: number): Promise<void> {
  reprocessingIds.add(eventId)
  try {
    await reprocessEvent(eventId)
    eventBus.emit('globalMessage.success', t('Evento reprocessado com sucesso'))
    if (props.franquiaId) {
      await fetchBilling360(props.franquiaId, props.isStaff)
    }
  } catch (err) {
    log('BillingThreeSixtyDetail', 'handleReprocess', 'Erro ao reprocessar evento', err)
    eventBus.emit('globalMessage.error', t('Erro ao reprocessar evento'))
  } finally {
    reprocessingIds.delete(eventId)
  }
}

async function handleSyncSubscription(subscriptionId: number): Promise<void> {
  syncingSubscriptionIds.add(subscriptionId)
  try {
    const result = await syncSubscription(subscriptionId)
    eventBus.emit('modal.reconciliation-result.open', {
      title: `${t('Assinatura')} #${subscriptionId}`,
      rows: result.rows,
      truncated: result.truncated,
    })
    eventBus.emit('globalMessage.success', t('Assinatura sincronizada com o provedor'))
  } catch (err) {
    log('BillingThreeSixtyDetail', 'handleSyncSubscription', 'Erro ao sincronizar assinatura', err)
    eventBus.emit('globalMessage.error', t('Erro ao sincronizar assinatura'))
  } finally {
    syncingSubscriptionIds.delete(subscriptionId)
  }
}

const setupFeeRows = computed(() => {
  const s = setupFeeStatus.value
  const statusLabels: Record<string, string> = {
    owed: t('Pendente'),
    paid: t('Paga'),
    exempt: t('Isenta'),
  }
  return [
    { prop: t('Status da taxa'), value: s?.status ? (statusLabels[s.status] ?? s.status) : t('Pendente') },
    { prop: t('Origem'), value: s?.source ? t(s.source) : '—' },
    { prop: t('ID da cobrança pendente'), value: s?.pending_charge_id ?? '—' },
    { prop: t('URL de checkout'), value: s?.checkout_url ?? '—' },
  ]
})

const setupFeeDisplayRows = computed(() =>
  (dados.value?.transactions ?? [])
    .filter((tx) => tx.kind === 'setup_fee')
    .map((tx) => {
      const currency = (tx.currency && /^[A-Z]{3}$/i.test(tx.currency)) ? tx.currency.toUpperCase() : 'BRL'
      let formattedAmount = ''
      try {
        formattedAmount = n(
          (tx.amount_cents ?? 0) / currencyFactor('pt-BR', currency),
          { style: 'currency', currency },
        )
      } catch {
        formattedAmount = `${(tx.amount_cents ?? 0) / 100}`
      }
      return {
        ...tx,
        id: tx.id,
        currency,
        amount_display: formattedAmount,
        provider_display: providerLabel(tx.provider),
        paid_at_display: tx.paid_at ? formatDt(tx.paid_at) : '—',
        created_at_display: formatDt(tx.created_at),
        can_reconcile: tx.status === 'pending',
      }
    }),
)


const setupFeeTableConfig = computed(() => ({
  tableId: 'billing360SetupChargesTable',
  columns: [
    {
      data: 'id',
      title: t('ID'),
      sortable: true,
      renderType: 'clickable_id',
    },
    {
      data: 'amount_display',
      title: t('Valor'),
      sortable: true,
    },
    {
      data: 'provider_display',
      title: t('Gateway'),
      sortable: true,
    },
    {
      data: 'provider_charge_id',
      title: t('ID Gateway'),
      sortable: true,
    },
    {
      data: 'status',
      title: t('Status'),
      sortable: true,
      renderType: 'badge',
      badgeConfig: txStatusBadgeConfig,
    },
    {
      data: 'paid_at_display',
      title: t('Pago em'),
      sortable: true,
    },
    {
      data: 'created_at_display',
      title: t('Criado em'),
      sortable: true,
    },
  ],
  pagination: false,
}))

function handleSetupFeeEditClick(id: unknown): void {
  const chargeId = Number(id)
  if (!chargeId) return
  const row = setupFeeDisplayRows.value.find((c) => Number(c.id) === chargeId)
  if (row) {
    eventBus.emit('modal.setup-fee-detail.open', {
      row,
      franquiaId: Number(props.franquiaId),
      franchiseName: `Franquia #${props.franquiaId}`,
    })
  }
}


async function handleSyncSetupFee(): Promise<void> {
  const fid = Number(props.franquiaId)
  if (!fid) return

  isSyncingSetupFee.value = true
  try {
    const result = await syncSetupFee(fid)
    eventBus.emit('modal.reconciliation-result.open', {
      title: `${t('Taxa de Implantação')} - ${t('Franquia')} #${fid}`,
      rows: result.rows,
      truncated: false,
    })
    eventBus.emit('globalMessage.success', t('Taxa de implantação sincronizada com o provedor'))
    await loadSetupFeeStatus(fid)
  } catch (err) {
    log('BillingThreeSixtyDetail', 'handleSyncSetupFee', 'Erro ao sincronizar taxa', err)
    eventBus.emit('globalMessage.error', t('Erro ao sincronizar taxa de implantação'))
  } finally {
    isSyncingSetupFee.value = false
  }
}

function handleEmitSetupFee(): void {
  eventBus.emit('modal.setup-fee-emit.open', { franquiaId: Number(props.franquiaId) })
}
function handleMarkPaidSetupFee(): void {
  eventBus.emit('modal.setup-fee-mark-paid.open', { franquiaId: Number(props.franquiaId) })
}
function handleExemptSetupFee(): void {
  eventBus.emit('modal.setup-fee-exempt.open', { franquiaId: Number(props.franquiaId) })
}
/**
 * Is this franchise's setup-fee obligation already satisfied?
 *
 * Satisfied means the emit/exempt pair is pointless — POST setup-fee/emit
 * refuses a satisfied obligation with 'setup_ja_satisfeito' (HTTP 409), which
 * is exactly the dead end staff hit here: the toolbar only recognised 'exempt',
 * so a PAID franchise still showed 'Emitir taxa' and 'Isentar taxa' and the
 * only way forward — 'Re-exigir taxa' — stayed hidden. Its modal was built and
 * registered in ModalManager, but nothing in the app emitted its open event, so
 * the action was unreachable (2026-08-21).
 *
 * The paid|exempt question comes from THE shared predicate
 * (src/utils/setupFeeStatus), never a local `=== 'exempt'` again. The null guard
 * is this screen's own concern and not the predicate's: while the status is
 * still loading we must not offer 'Re-exigir' on a franchise that may owe.
 */
const setupFeeSatisfied = computed(() => {
  const status = setupFeeStatus.value?.status
  if (status == null || status === '') return false
  return isSetupSatisfied({ setup_status: status })
})

function handleRerequireSetupFee(): void {
  eventBus.emit('modal.setup-fee-rerequire.open', { franquiaId: Number(props.franquiaId) })
}

/**
 * Refetch everything this modal shows — the handler behind every table's
 * 'Atualizar' button and every '*.saved' event.
 *
 * props.isStaff MUST be forwarded: fetchBilling360 defaults it to true, so the
 * bare call sent a franqueado to the STAFF admin endpoint, which answers 403
 * and empties the modal. loadFranchise always passed it; this path did not
 * (found 2026-08-20 while giving the user-facing tables their refresh button).
 */
function reloadAll(): void {
  if (props.franquiaId) {
    fetchBilling360(props.franquiaId, props.isStaff)
    loadSetupFeeStatus(Number(props.franquiaId))
  }
}

onMounted(() => {
  eventBus.on('graceGrant.saved', reloadAll)
  eventBus.on('graceRevoke.saved', reloadAll)
  eventBus.on('subscribe.saved', reloadAll)
  eventBus.on('subscription.saved', reloadAll)
  eventBus.on('subscriptionDetail.saved', reloadAll)
  eventBus.on('staffCreditsTopup.saved', reloadAll)
  eventBus.on('staffTransaction.saved', reloadAll)
  eventBus.on('setupFee.saved', reloadAll)
  eventBus.on('credit.topup.confirmed', reloadAll)
})

onBeforeUnmount(() => {
  eventBus.off('graceGrant.saved', reloadAll)
  eventBus.off('graceRevoke.saved', reloadAll)
  eventBus.off('subscribe.saved', reloadAll)
  eventBus.off('subscription.saved', reloadAll)
  eventBus.off('subscriptionDetail.saved', reloadAll)
  eventBus.off('staffCreditsTopup.saved', reloadAll)
  eventBus.off('staffTransaction.saved', reloadAll)
  eventBus.off('setupFee.saved', reloadAll)
  eventBus.off('credit.topup.confirmed', reloadAll)
})
</script>

<style scoped>
.billing-360-hint {
  display: block;
  margin-top: 0.35rem;
  font-size: 0.8125rem;
  color: var(--p-text-muted-color, #6b7280);
}
.billing-360-toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.billing-360-credits-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.billing-360-loading,
.billing-360-error,
.billing-360-empty {
  padding: 1rem 0;
  color: var(--p-text-muted-color, #64748b);
}

.billing-360-error {
  color: var(--p-red-600, #dc2626);
}

.billing-360-section {
  margin-top: 1.5rem;
}

.billing-360-section-actions {
  margin-top: 0.75rem;
}

.billing-360-section-title {
  margin: 0 0 0.75rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--p-text-color, #1e293b);
}

.billing-360-tx-accordion {
  margin-top: 1rem;
}

.billing-360-tx-block {
  border: 1px solid var(--p-surface-200, #e2e8f0);
  border-radius: 0.375rem;
  margin-bottom: 0.5rem;
  overflow: hidden;
}

.billing-360-tx-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.6rem 1rem;
  cursor: pointer;
  background: var(--p-surface-50, #f8fafc);
  font-size: 0.85rem;
}

.billing-360-tx-header:hover {
  background: var(--p-surface-100, #f1f5f9);
}

.billing-360-tx-date   { flex: 1.5; }
.billing-360-tx-kind   { flex: 1; }
.billing-360-tx-amount { flex: 1; font-weight: 600; }
.billing-360-tx-status { flex: 1; }
.billing-360-tx-provider { flex: 1; }
.billing-360-tx-chevron { margin-left: auto; color: var(--p-text-muted-color, #64748b); }

.billing-360-attempts {
  padding: 0.75rem 1rem;
  background: var(--p-surface-0, #fff);
}

.billing-360-attempts-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.billing-360-attempts-table th,
.billing-360-attempts-table td {
  text-align: left;
  padding: 0.35rem 0.5rem;
  border-bottom: 1px solid var(--p-surface-200, #e2e8f0);
}

.billing-360-attempts-table th {
  font-weight: 600;
  color: var(--p-text-muted-color, #64748b);
}

.billing-360-event-block {
  border: 1px solid var(--p-surface-200, #e2e8f0);
  border-radius: 0.375rem;
  margin-bottom: 0.5rem;
  overflow: hidden;
}

.billing-360-event-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.6rem 1rem;
  cursor: pointer;
  background: var(--p-surface-50, #f8fafc);
  font-size: 0.85rem;
}

.billing-360-event-header:hover {
  background: var(--p-surface-100, #f1f5f9);
}

.billing-360-event-type     { flex: 2; font-weight: 600; }
.billing-360-event-status   { flex: 1; }
.billing-360-event-provider { flex: 1; }
.billing-360-event-date     { flex: 1.5; color: var(--p-text-muted-color, #64748b); }
.billing-360-event-chevron  { margin-left: auto; color: var(--p-text-muted-color, #64748b); }

.billing-360-event-payload {
  padding: 0.75rem 1rem;
  background: var(--p-surface-0, #fff);
  font-size: 0.82rem;
}

.billing-360-event-meta,
.billing-360-event-error {
  margin-bottom: 0.5rem;
}

.billing-360-event-error {
  color: var(--p-red-600, #dc2626);
}

.billing-360-event-actions {
  margin-bottom: 0.5rem;
}

.billing-360-event-json {
  background: var(--p-surface-100, #f1f5f9);
  border-radius: 0.25rem;
  padding: 0.5rem;
  font-size: 0.78rem;
  overflow-x: auto;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>

